import React from 'react'
import styled from '@emotion/styled'
import { get, isEmpty, isEqual, isNil } from 'lodash-es'
import { ReactReduxContext } from 'react-redux'
import { ICellEditor, ICellEditorParams, IRowNode } from 'ag-grid-community'
import { CellPosition, RowDataUpdatedEvent } from 'ag-grid-community'
import { Focusable, InputProps, TextInput } from 'view/components/inputs'
import { RowDataGetter } from 'view/shared/datagrid'
import { getIssuesForField } from 'model'
import { KeyCodes } from 'utils'

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 3px;
`

type CellInputComponentType = new () => React.Component<InputProps> & Focusable

export interface DefaultCellEditorProps extends ICellEditorParams {
  key: string
  getNodeId?: (data: any) => {}
  getValidationIssues?: (data: any) => []
  inputComponent: CellInputComponentType
  inputParams?: (data: any, context: any) => {}
  defaultField?: string | ((data: any) => string)
  validationField?: string | ((data: any) => string)
  updateField: string | ((data: any) => string)
  isModifiedField?: string | ((data: any) => string)
  rowDataGetter?: RowDataGetter<any>
  forceUpdateOnNull?: boolean
  useLoseEquality?: boolean
  useEnterForNavigation?: boolean
}

interface DefaultCellEditorState {
  value: any
  cellValueEdited: boolean
}

enum Direction {
  NEXT = 'NEXT',
  PREVIOUS = 'PREVIOUS'
}

export class DefaultCellEditor extends React.Component<DefaultCellEditorProps, DefaultCellEditorState> implements ICellEditor {
  constructor(props: DefaultCellEditorProps) {
    super(props)
    this.state = {
      value: this.getFieldValue(),
      cellValueEdited: false
    }
  }

  render() {
    const { inputComponent, inputParams, node, rowDataGetter } = this.props
    const InputComponent = inputComponent ? inputComponent : TextInput
    const rowParams = this.getRowParams()
    const rowData = rowDataGetter ? rowDataGetter(rowParams, true) : node.data

    if (isNil(rowData) || isEmpty(rowData)) {
      return null
    }

    const inputProps = inputParams ? inputParams(rowData, rowParams.context) : {}

    return (
      <ReactReduxContext.Provider value={rowParams.context.reduxContext}>
        <Container onBlur={this.handleBlur} onKeyDown={this.handleKeyDown}>
          <InputComponent
            {...inputProps}
            ref={this.handleRef}
            value={this.state.value}
            validationIssues={this.getFieldValidationIssues()}
            onChange={this.handleChange}
            onUpdate={this.handleUpdate}
          />
        </Container>
      </ReactReduxContext.Provider>
    )
  }

  componentDidUpdate(prevProps: DefaultCellEditorProps) {
    if (!isEqual(prevProps, this.props) && !this.state.cellValueEdited) {
      this.setState({ value: this.getFieldValue() })
    }
  }

  componentDidMount() {
    if (this.props.api) {
      this.props.api.addEventListener('rowDataUpdated', this.handleDataUpdated)
    } else {
      throw new Error('Grid API is not set')
    }

    if (this.props.cellStartedEdit) {
      setTimeout(() => this.focusIn(), 0)
    }
  }

  private unmounted = false

  componentWillUnmount() {
    this.unmounted = true

    if (this.props.api) {
      !this.props.api.isDestroyed() && this.props.api.removeEventListener('rowDataUpdated', this.handleDataUpdated)
    } else {
      throw new Error('Grid API is not set')
    }
  }

  private getRowParams = () => this.props

  private getNodeId = () => {
    const { node, getNodeId, rowDataGetter } = this.props
    const rowData = rowDataGetter ? rowDataGetter(this.getRowParams(), true) : node.data
    return getNodeId ? getNodeId(rowData) : node.id
  }

  private getField = (field: string | ((data: any) => string)) => {
    const { node, rowDataGetter } = this.props
    const rowData = rowDataGetter ? rowDataGetter(this.getRowParams(), true) : node.data
    return typeof field === 'string' ? field : field(rowData)
  }

  private getFieldValue = () => {
    const { node, defaultField, updateField, isModifiedField, rowDataGetter } = this.props
    const updateFieldName = this.getField(updateField)

    if (!updateFieldName) {
      throw new Error('Editable columns must specify the updateField')
    }

    if (isNil(defaultField) !== isNil(isModifiedField)) {
      throw new Error('Editable columns must specify defaultField and isModifiedField together')
    }

    const rowData = rowDataGetter ? rowDataGetter(this.getRowParams(), true) : node.data
    const updateFieldValue = get(rowData, updateFieldName)
    const defaultFieldValue = defaultField ? get(rowData, this.getField(defaultField)) : null
    const showUpdateFieldValue = !isModifiedField || get(rowData, this.getField(isModifiedField))
    return showUpdateFieldValue ? updateFieldValue : defaultFieldValue
  }

  private getValidationIssues = () => {
    const { getValidationIssues, node, rowDataGetter } = this.props
    const rowData = rowDataGetter ? rowDataGetter(this.getRowParams(), true) : node.data
    return getValidationIssues ? getValidationIssues(rowData) : rowData.validationIssues
  }

  private getFieldValidationIssues = () => {
    const { validationField, updateField } = this.props
    const fieldName = this.getField(validationField || updateField)
    const validationIssues = this.getValidationIssues()
    return getIssuesForField(validationIssues || [], fieldName)
  }

  private input: Focusable | null = null
  private handleRef = (ref: Focusable | null) => (this.input = ref)

  private handleChange = () => {
    const { context, node, updateField, rowDataGetter } = this.props
    const alreadyEdited = this.state.cellValueEdited

    if (!alreadyEdited) {
      this.setState({ cellValueEdited: true })
      const rowData = rowDataGetter ? rowDataGetter(this.getRowParams(), true) : node.data
      context.changeCellValue(this.getNodeId(), rowData, this.getField(updateField))
    }
  }

  private handleUpdate = (newValue: string, rawValue?: string) => {
    const { context, node, updateField, rowDataGetter, forceUpdateOnNull } = this.props

    this.handleChange()

    this.setState({ value: newValue })
    if (!this.areEqual(newValue, this.getFieldValue()) || (forceUpdateOnNull && newValue === null)) {
      const rowData = rowDataGetter ? rowDataGetter(this.getRowParams(), true) : node.data
      context.updateCellValue(this.getNodeId(), rowData, this.getField(updateField), newValue, rawValue)
    }
  }

  private areEqual = (newValue: any, currentValue: any) => {
    // eslint-disable-next-line eqeqeq
    return this.props.useLoseEquality ? newValue == currentValue : newValue === currentValue
  }

  private handleDataUpdated = (_evt: RowDataUpdatedEvent) => {
    /* Helpfully ag-grid sometimes calls event handlers after they have been unregistered */
    if (!this.unmounted && !this.state.cellValueEdited) {
      this.setState({ value: this.getFieldValue() })
    } else {
      this.forceUpdate()
    }
  }

  private canEditNextRow = (nextRow: IRowNode) => {
    const { context, rowDataGetter } = this.props

    if (!context.canEditRow) {
      return true
    }

    const rowData = rowDataGetter
      ? rowDataGetter(
          {
            ...this.props,
            node: nextRow,
            data: nextRow.data
          },
          true
        )
      : nextRow.data

    return context.canEditRow(rowData)
  }

  private getNearestEditableRowIndex = (currentCell: CellPosition, direction: Direction): number | null => {
    const { api } = this.props

    if (!api) {
      return null
    }

    let rowIndex = currentCell.rowIndex

    while (true) {
      direction === Direction.NEXT ? rowIndex++ : rowIndex--

      const nextRow = api.getDisplayedRowAtIndex(rowIndex)

      if (!nextRow) {
        return null
      }

      const isNextRowEditable = currentCell?.column.isCellEditable(nextRow) && this.canEditNextRow(nextRow)

      if (isNextRowEditable) {
        return nextRow.rowIndex
      }
    }
  }

  private handleKeyDown = (evt: React.KeyboardEvent) => {
    const currentCell = this.props.api?.getFocusedCell()
    const api = this.props.api

    if (!currentCell || !api) {
      return
    }

    if (evt.keyCode === KeyCodes.ENTER) {
      this.props.stopEditing()

      if (this.props.useEnterForNavigation) {
        const nextRowEditableCellIndex = this.getNearestEditableRowIndex(currentCell, Direction.NEXT)

        if (!isNil(nextRowEditableCellIndex)) {
          setTimeout(() => {
            api.setFocusedCell(nextRowEditableCellIndex, currentCell.column.getColId())
            api.startEditingCell({ rowIndex: nextRowEditableCellIndex, colKey: currentCell.column.getColId() })
          }, 0)
        }
      }
    }

    switch (evt.keyCode) {
      case KeyCodes.UP:
        const previousRowEditableCellIndex = this.getNearestEditableRowIndex(currentCell, Direction.PREVIOUS)

        if (!isNil(previousRowEditableCellIndex)) {
          setTimeout(() => {
            api.setFocusedCell(previousRowEditableCellIndex, currentCell.column.getColId())
            api.startEditingCell({ rowIndex: previousRowEditableCellIndex, colKey: currentCell.column.getColId() })
          }, 0)
        }
        break

      case KeyCodes.DOWN:
        const nextRowEditableCellIndex = this.getNearestEditableRowIndex(currentCell, Direction.NEXT)

        if (!isNil(nextRowEditableCellIndex)) {
          setTimeout(() => {
            api.setFocusedCell(nextRowEditableCellIndex, currentCell.column.getColId())
            api.startEditingCell({ rowIndex: nextRowEditableCellIndex, colKey: currentCell.column.getColId() })
          }, 0)
        }
        break
      default:
        return
    }
  }

  private handleBlur = () => {
    this.setState({ cellValueEdited: false })
  }

  /* ICellEditor */

  public getValue() {
    return this.state.value
  }

  public focusIn = () => {
    if (this.input) {
      this.input.setFocus()
    }
  }
}
